// src/oura/components/docs/write/ClientEmailView.tsx
//
// The client email for a finished doc (epic ourteam-backend#6697), a view of
// the doc drawer like the review email. It opens only for a doc staff marked
// Done that the client can open; the server refuses every other.
//
// Oura reads the doc and writes the draft by the ops guide ("Oura Client Email
// Generator"): one email, the doc's link, 3 to 4 highlights from the doc, what
// we need from the client, the reply-by and the next step. The draft Oura keeps
// for the doc as it stands opens at once; a new one is a paid AI call.
//
//   Preview   the email as the client's inbox shows it: the server's HTML, the
//             same bytes the send hands to the mail provider. This view never
//             renders the markup itself, so the preview cannot differ.
//   Edit email  a dialog (EditClientEmailDialog, #4840): who it goes to, the
//             subject, and the body in a rich text editor (bold, lists, the
//             doc's link). Saved, it becomes the email's markup; the server
//             previews it and runs the guide's checks again, and Send sends it.
//   Send      a confirmation dialog first (SendClientEmailDialog). A red
//             check holds Send; an amber one is for the person reading.

import { useEffect, useRef, useState } from "react";
import { AlertTriangleIcon, CheckIcon, FlaskConicalIcon, PencilLineIcon, RefreshCwIcon, SendIcon, XIcon } from "lucide-react";
import {
    clearClientEmail,
    draftClientEmail,
    getClientEmail,
    previewClientEmail,
    saveClientEmail,
    sendClientEmail,
    sendClientEmailTest,
    type ClientEmailCheck,
    type ClientEmailDraft,
    type ClientEmailPurpose,
    type ClientEmailSaved,
    type ClientEmailState,
    type DocMessagePerson,
    type SentDocMessage,
} from "@service/aeo/clientDocsWrite";
import type { DocsFailure, StaffDocument } from "@service/aeo/clientDocs";
import { cn } from "../../../lib/cn";
import { DrawerBlock, DrawerFold, DrawerFoot, DrawerPulse, PillButton, SURFACE, TYPE, type DrawerFootMessage } from "@oura/ui";
import { docDate, docsFailureText } from "@oura/docs";
import { formatPeople } from "../../../onboarding/drafts";
import { readRecipients, sameMessage, toMessageText, type MessageText } from "../../../onboarding/messageText";
import { SenderLine } from "../../onboarding/fields";
import { EditClientEmailDialog } from "./EditClientEmailDialog";
import { PREVIEW_PAGE_CSS } from "./emailStyle";
import { SendClientEmailDialog } from "./SendClientEmailDialog";
import { TestClientEmailDialog } from "./TestClientEmailDialog";
import type { DocWriteView } from "./model";
import { useSignedInName } from "@oura/docs/signedIn";

/** What staff typed, kept while the view is closed, by doc. Sending clears it. The server keeps the saved
 *  email too (`saved`), so a refresh or another tab opens on it; this is only the in-tab copy. */
const edits = new Map<string, MessageText>();

/** A saved email with no Oura draft kept for its purpose, as the draft the view shows and sends from. */
function draftFromSaved(saved: ClientEmailSaved): ClientEmailDraft {
    return {
        draftId: "",
        purpose: saved.purpose,
        madeFrom: null,
        madeAt: null,
        to: saved.to,
        cc: saved.cc,
        subject: saved.subject,
        body: saved.body,
        html: saved.html,
        checks: saved.checks,
        dropped: 0,
    } as ClientEmailDraft;
}

type Load = { status: "loading" } | { status: "ready"; state: ClientEmailState } | { status: "error"; failure: DocsFailure };
type Shown = { html: string; checks: ClientEmailCheck[] };

/** The email's HTML in a frame that runs nothing: no scripts, no forms, and its links take no click. */
export function PreviewFrame({ html, subject }: { html: string; subject: string }) {
    const ref = useRef<HTMLIFrameElement>(null);
    const [height, setHeight] = useState(360);
    const doc = `<!doctype html><html><head><meta charset="utf-8"><style>${PREVIEW_PAGE_CSS}</style></head><body>${html}</body></html>`;
    return (
        <div className={cn(SURFACE.inner, "p-0")}>
            <p className={cn(TYPE.tc, "m-0 border-b border-gray-100 px-4 py-2 font-medium text-gray-900")}>{subject || "(no subject)"}</p>
            <iframe
                ref={ref}
                title="The email as the client gets it"
                // Same origin only so the frame's height can be read; no allow-scripts, so nothing in it runs.
                sandbox="allow-same-origin"
                srcDoc={doc}
                className="block w-full rounded-b-lg bg-white"
                style={{ height }}
                onLoad={() => {
                    const h = ref.current?.contentDocument?.documentElement.scrollHeight;
                    if (h) setHeight(Math.min(Math.max(h + 4, 160), 900));
                }}
            />
        </div>
    );
}

/** The Checks card, hidden at ops' request (3 Nov 2026). The blocking checks still stop Send; the first one
 *  that fails is said beside the button instead. Flip to show the card again. */
const CHECKS_CARD_ON = false;

function Checks({ checks, dropped }: { checks: ClientEmailCheck[]; dropped: number }) {
    const failed = checks.filter((c) => !c.ok);
    const blocking = failed.some((c) => c.level === "block");
    return (
        <DrawerFold
            id="staff-doc.client-email-checks"
            label="Checks"
            note={failed.length === 0 ? "All checks passed" : blocking ? "Fix the red ones to send" : `${failed.length} to look at`}
            defaultOpen={failed.length > 0}
        >
            <ul className="m-0 list-plain space-y-1.5 p-0">
                {checks.map((c) => (
                    <li key={c.id} className={cn(TYPE.tc, "flex items-start gap-2", c.ok ? "text-gray-600" : c.level === "block" ? "text-rose-700" : "text-amber-800")}>
                        {c.ok ? (
                            <CheckIcon className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-emerald-600" aria-hidden />
                        ) : c.level === "block" ? (
                            <XIcon className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" aria-hidden />
                        ) : (
                            <AlertTriangleIcon className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" aria-hidden />
                        )}
                        <span>{c.text}</span>
                    </li>
                ))}
            </ul>
            {dropped > 0 && (
                <p className={cn(TYPE.tc, "m-0 mt-2 text-gray-500")}>
                    Oura left out {dropped === 1 ? "1 point" : `${dropped} points`} it could not find in the doc.
                </p>
            )}
            <p className={cn(TYPE.tc, "m-0 mt-2 text-gray-500")}>Read the highlights against the doc: Oura took them from it, but you send them.</p>
        </DrawerFold>
    );
}

export function ClientEmailView({ doc, onBusy, onView }: { doc: StaffDocument; onBusy: (busy: boolean) => void; onView: (view: DocWriteView | "doc") => void }) {
    const name = useSignedInName();
    const [load, setLoad] = useState<Load>({ status: "loading" });
    const [purpose, setPurpose] = useState<ClientEmailPurpose | null>(null);
    const [draft, setDraft] = useState<ClientEmailDraft | null>(null);
    const [drafting, setDrafting] = useState(false);
    const [draftFailure, setDraftFailure] = useState<DocsFailure | null>(null);
    const [askFresh, setAskFresh] = useState(false);
    const [text, setText] = useState<MessageText | null>(() => edits.get(doc.id) ?? null);
    const [shown, setShown] = useState<Shown | null>(null);
    const [editing, setEditing] = useState(false);
    const [confirming, setConfirming] = useState(false);
    const [sending, setSending] = useState(false);
    const [sendFailure, setSendFailure] = useState<string | null>(null);
    const [unconfirmed, setUnconfirmed] = useState(false);
    const [message, setMessage] = useState<DrawerFootMessage | null>(null);
    const [sent, setSent] = useState(false);
    // The email as it went, from the send's answer: shown as the client got it.
    const [sentMessage, setSentMessage] = useState<SentDocMessage | null>(null);
    const [testing, setTesting] = useState(false);
    const [confirmingTest, setConfirmingTest] = useState(false);
    const [testFailure, setTestFailure] = useState<string | null>(null);
    const [tries, setTries] = useState(0);
    // One per press of Send: a retry of the same press never sends twice (the server keeps it).
    const sendKey = useRef<string | null>(null);

    const write = (p: ClientEmailPurpose, fresh = false) => {
        setDrafting(true);
        setDraftFailure(null);
        void draftClientEmail(doc.id, { purpose: p, fresh }).then((r) => {
            setDrafting(false);
            if (r.ok) {
                // The answer is the source of truth for what the draft is for.
                setPurpose(r.data.purpose);
                setDraft(r.data);
                setShown({ html: r.data.html, checks: r.data.checks });
            } else setDraftFailure(r);
        });
    };

    useEffect(() => {
        let alive = true;
        setLoad({ status: "loading" });
        void getClientEmail(doc.id).then((r) => {
            if (!alive) return;
            if (!r.ok) {
                setLoad({ status: "error", failure: r });
                return;
            }
            setLoad({ status: "ready", state: r.data });
            setPurpose(r.data.purpose);
            if (!r.data.eligible) return;
            // The email staff saved opens as they left it, on its purpose, and Oura does not write it
            // again: not after a refresh, nor after the Google Doc changed.
            const saved = r.data.saved;
            if (saved) {
                const kept = toMessageText({ to: saved.to, cc: saved.cc, subject: saved.subject, body: saved.body });
                edits.set(doc.id, kept);
                setText(kept);
                setPurpose(saved.purpose);
                // No draft kept for its purpose: the saved email stands in for it, never a new paid draft (Jack).
                setDraft(r.data.draft ?? draftFromSaved(saved));
                setShown({ html: saved.html, checks: saved.checks });
                return;
            }
            if (r.data.draft) {
                setDraft(r.data.draft);
                setShown({ html: r.data.draft.html, checks: r.data.draft.checks });
            } else write(r.data.purpose);
        });
        return () => {
            alive = false;
        };
        // `write` is stable enough here: it only reads the doc id this view is keyed by.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [doc.id, tries]);

    const oura = draft ? toMessageText({ to: draft.to, cc: draft.cc, subject: draft.subject, body: draft.body }) : null;
    const value = text ?? oura;
    const edited = Boolean(value && oura && !sameMessage(value, oura));

    // Each change is previewed by the server, which also checks it again; a pause in typing sends it.
    // A preview belongs to the exact text it was made from (Jack, #4839): an answer for an older
    // edit is dropped, and Send waits until the text on screen has its own preview.
    const previewInput = value && draft && edited ? JSON.stringify({ to: value.to, subject: value.subject, body: value.body }) : null;
    const previewSequence = useRef(0);
    const [previewedFor, setPreviewedFor] = useState<string | null>(null);
    const [previewFailure, setPreviewFailure] = useState<string | null>(null);
    useEffect(() => {
        const sequence = ++previewSequence.current;
        if (!value || !draft || !previewInput) {
            setPreviewedFor(null);
            setPreviewFailure(null);
            if (draft) setShown({ html: draft.html, checks: draft.checks });
            return;
        }
        setPreviewFailure(null);
        const people = readRecipients(value);
        const t = window.setTimeout(() => {
            void previewClientEmail(doc.id, { subject: value.subject, body: value.body, to: people.draft ? people.draft.to : [] }).then((r) => {
                if (sequence !== previewSequence.current) return;
                if (r.ok) {
                    setShown(r.data);
                    setPreviewedFor(previewInput);
                } else {
                    setPreviewedFor(null);
                    setPreviewFailure(docsFailureText(r, "the preview"));
                }
            });
        }, 600);
        return () => window.clearTimeout(t);
    }, [doc.id, draft, previewInput]); // eslint-disable-line react-hooks/exhaustive-deps
    const previewCurrent = !edited || previewedFor === previewInput;

    const back = (primary = false) => (
        <PillButton variant={primary ? "primary" : "secondary"} disabled={sending} onClick={() => onView("doc")}>
            Back to the doc
        </PillButton>
    );

    if (load.status !== "ready") {
        return (
            <>
                <DrawerBlock>
                    {load.status === "loading" ? <DrawerPulse lines={3} label="Opening the client email" /> : <p className="m-0 text-gray-700">{docsFailureText(load.failure, "the client email")}</p>}
                </DrawerBlock>
                <DrawerFoot first={load.status === "error" ? <PillButton writes onClick={() => setTries((n) => n + 1)}>Try again</PillButton> : undefined} second={back()} />
            </>
        );
    }

    const state = load.state;
    if (!state.eligible) {
        const notShared = state.refusal?.code === "not_shared";
        return (
            <>
                <DrawerBlock>
                    <p className="m-0 text-gray-700">{state.refusal?.message ?? "This doc can't have its client email yet."}</p>
                    <p className={cn(TYPE.tc, "m-0 mt-1 text-gray-500")}>The client email is for a finished doc: marked Done and shared with the client.</p>
                </DrawerBlock>
                <DrawerFoot
                    first={notShared ? <PillButton variant="primary" onClick={() => onView("share")}>Share with the client</PillButton> : undefined}
                    second={back()}
                />
            </>
        );
    }

    const change = (next: MessageText) => {
        setMessage(null);
        setText(next);
        edits.set(doc.id, next);
        // Kept on the server too, so a refresh or another tab opens on it.
        const people = readRecipients(next);
        if (!purpose || !people.draft) return;
        void saveClientEmail(doc.id, { purpose, to: people.draft.to, cc: people.draft.cc, subject: next.subject, body: next.body }).then((r) => {
            if (!r.ok) setMessage({ tone: "error", text: `Your changes are not saved yet: ${docsFailureText(r, "the email")}` });
        });
    };

    const people = value ? readRecipients(value) : null;
    // The guide's checks warn, they never hold Send (3 Nov 2026, ops: "warning, not blocking"): the server
    // no longer refuses them either. The important ones that fail are said by the button.
    const failedChecks = previewCurrent ? (shown?.checks ?? []).filter((c) => c.level === "block" && !c.ok) : [];
    const blockedMessage: DrawerFootMessage | null =
        !CHECKS_CARD_ON && failedChecks.length > 0 && !sent
            ? {
                  tone: "info",
                  text: `Check before sending: ${failedChecks[0].text}${failedChecks.length > 1 ? ` (and ${failedChecks.length - 1} more)` : ""}`,
              }
            : null;
    const capStopped = state.cap.stopped;

    const openConfirm = () => {
        if (!previewCurrent) {
            setMessage({ tone: "error", text: previewFailure ?? "Wait for the preview of your latest change." });
            return;
        }
        if (!value || !people?.draft) {
            setEditing(true);
            setMessage({ tone: "error", text: people?.errors?.to ?? people?.errors?.cc ?? "Add who this goes to." });
            return;
        }
        sendKey.current = crypto.randomUUID();
        setSendFailure(null);
        setUnconfirmed(false);
        setConfirming(true);
    };

    const send = async (resendUnknown: boolean) => {
        if (!value || !people?.draft || !purpose) return;
        // Sending over an unknown outcome is a new press, with its own key.
        if (resendUnknown || !sendKey.current) sendKey.current = crypto.randomUUID();
        setSending(true);
        setSendFailure(null);
        onBusy(true);
        const r = await sendClientEmail(doc.id, {
            purpose,
            to: people.draft.to,
            cc: people.draft.cc,
            subject: value.subject.trim(),
            body: value.body,
            draftId: draft?.draftId || null,
            sendKey: sendKey.current,
            resendUnknown,
        });
        setSending(false);
        onBusy(false);
        if (!r.ok) {
            const code = r.reason === "rejected" ? r.code : null;
            setUnconfirmed(code === "send_unconfirmed");
            setSendFailure(docsFailureText(r, "the email"));
            return;
        }
        edits.delete(doc.id);
        setText(null);
        setConfirming(false);
        setSent(true);
        setSentMessage(r.data);
        setMessage({ tone: "success", text: `Sent to ${formatPeople(r.data.to)}.` });
    };

    // A test of the email as it stands, to our own test inbox only: nothing to the client, nothing kept
    // (#4842). It asks first, as Send does (#4844); a refusal stays in the dialog.
    const sendTest = async () => {
        if (!value || !previewCurrent) return;
        setTesting(true);
        setTestFailure(null);
        setMessage(null);
        const r = await sendClientEmailTest(doc.id, { subject: value.subject.trim(), body: value.body });
        setTesting(false);
        if (!r.ok) {
            setTestFailure(docsFailureText(r, "the test"));
            return;
        }
        setConfirmingTest(false);
        setMessage({ tone: "success", text: `Test sent to ${r.data.to}. Nothing went to the client.` });
    };

    // Another purpose is another draft: the old one goes first, so a failed switch never leaves
    // the old email sendable under the new purpose (Jack, #4839).
    const pickPurpose = (p: ClientEmailPurpose) => {
        if (p === purpose || drafting) return;
        // A saved edit is never dropped by a click on another purpose: going back to Oura's draft comes first.
        if (edited) {
            setMessage({ tone: "info", text: "Your edited email is saved. Go back to Oura's draft first to write it for something else." });
            return;
        }
        setPurpose(p);
        edits.delete(doc.id);
        setText(null);
        setDraft(null);
        setShown(null);
        setDraftFailure(null);
        setMessage(null);
        write(p);
    };

    const last = state.last;
    const first = sent ? (
        back(true)
    ) : (
        <PillButton variant="primary" icon={SendIcon} writes disabled={!draft || drafting || sending || !previewCurrent} onClick={openConfirm}>
            Send to the client
        </PillButton>
    );
    const second = sent ? undefined : edited ? (
        <PillButton
            disabled={sending}
            writes
            onClick={() => {
                setText(null);
                edits.delete(doc.id);
                void clearClientEmail(doc.id);
            }}
        >
            Back to Oura&rsquo;s draft
        </PillButton>
    ) : (
        back()
    );

    return (
        <>
            <DrawerBlock label="What it is for">
                <div className="mt-2 flex flex-wrap gap-2">
                    {state.purposes.map((p) => (
                        <PillButton key={p.key} size="sm" variant={p.key === purpose ? "primary" : "secondary"} writes disabled={drafting || sending || sent} onClick={() => pickPurpose(p.key)}>
                            {p.label}
                        </PillButton>
                    ))}
                </div>
                <p className={cn(TYPE.tc, "m-0 mt-2 text-gray-500")}>Oura writes it from the doc, in the guide&rsquo;s words. Nothing goes until you send it.</p>
            </DrawerBlock>

            {last && (
                <DrawerFold id="staff-doc.client-email-last" label="Last client email" note={`${docDate(last.sentAt) ?? ""} by ${last.sentBy}`.trim()}>
                    <p className={cn(TYPE.tc, "m-0 mb-2 text-gray-500")}>To {formatPeople(last.to)}</p>
                    {/* As it went: the server's HTML of it, never its markup (#4842). */}
                    {last.html ? (
                        <PreviewFrame html={last.html} subject={last.subject} />
                    ) : (
                        <>
                            <p className={cn(TYPE.tb, "m-0 font-medium text-gray-900")}>{last.subject}</p>
                            <p className={cn(TYPE.tc, "m-0 mt-1 whitespace-pre-wrap break-words text-gray-700")}>{last.body}</p>
                        </>
                    )}
                </DrawerFold>
            )}

            {drafting && (
                <DrawerBlock>
                    <DrawerPulse lines={5} label="Oura is reading the doc and writing the email" />
                </DrawerBlock>
            )}

            {!drafting && draftFailure && (
                <DrawerBlock>
                    <p className="m-0 text-gray-700">{docsFailureText(draftFailure, "the email")}</p>
                    {purpose && (
                        <div className="mt-2">
                            <PillButton size="sm" writes onClick={() => write(purpose)}>
                                Try again
                            </PillButton>
                        </div>
                    )}
                </DrawerBlock>
            )}

            {/* Sent: the email as the client got it, rendered, never its markup. */}
            {sent && sentMessage?.html && (
                <>
                    <p className={cn(TYPE.tc, "m-0 break-words text-gray-500")}>Sent to {formatPeople(sentMessage.to)}</p>
                    <PreviewFrame html={sentMessage.html} subject={sentMessage.subject} />
                </>
            )}

            {!drafting && draft && value && !sent && (
                <>
                    <div className="flex flex-wrap items-center justify-between gap-2">
                        <p className={cn(TYPE.tc, "m-0 min-w-0 break-words text-gray-500")}>
                            To {people?.draft ? formatPeople(people.draft.to) : value.to || "nobody yet"}
                            {people?.draft && people.draft.cc.length > 0 ? ` · Copy ${formatPeople(people.draft.cc)}` : ""}
                        </p>
                        {/* The editor starts from the preview of the text as it stands, so it waits for that preview. */}
                        <div className="flex flex-wrap gap-2">
                            <PillButton size="sm" icon={FlaskConicalIcon} busy={testing} writes disabled={sending || !previewCurrent || !shown} onClick={() => {
                                    setTestFailure(null);
                                    setConfirmingTest(true);
                                }} title="Sends this email to our own test inbox. Nothing goes to the client.">
                                Send a test
                            </PillButton>
                            <PillButton size="sm" icon={PencilLineIcon} disabled={sending || testing || !previewCurrent || !shown} onClick={() => setEditing(true)}>
                                Edit email
                            </PillButton>
                        </div>
                    </div>
                    {/* Never an older preview beside newer text: until this text has its own, say so. */}
                    {previewCurrent && shown ? (
                        <PreviewFrame html={shown.html} subject={value.subject} />
                    ) : previewFailure ? (
                        <DrawerBlock>
                            <p className="m-0 text-gray-700">{previewFailure}</p>
                        </DrawerBlock>
                    ) : (
                        <DrawerBlock>
                            <DrawerPulse lines={4} label="Updating the preview" />
                        </DrawerBlock>
                    )}

                    {CHECKS_CARD_ON && previewCurrent && shown && <Checks checks={shown.checks} dropped={draft.dropped} />}

                    <DrawerBlock>
                        {askFresh ? (
                            <div className="flex flex-wrap items-center gap-2">
                                <p className={cn(TYPE.tc, "m-0 text-gray-700")}>Oura reads the doc again: a paid AI call. Your edits are dropped.</p>
                                <PillButton
                                    size="sm"
                                    writes
                                    onClick={() => {
                                        setAskFresh(false);
                                        edits.delete(doc.id);
                                        setText(null);
                                        void clearClientEmail(doc.id);
                                        if (purpose) write(purpose, true);
                                    }}
                                >
                                    Read it again
                                </PillButton>
                                <PillButton size="sm" variant="text" onClick={() => setAskFresh(false)}>
                                    Cancel
                                </PillButton>
                            </div>
                        ) : capStopped ? (
                            <p className={cn(TYPE.tc, "m-0 text-gray-500")}>AI summaries and client emails have reached this month&rsquo;s cap, so Oura can&rsquo;t write a new draft.</p>
                        ) : (
                            <PillButton size="sm" variant="text" icon={RefreshCwIcon} writes onClick={() => setAskFresh(true)}>
                                Make a new draft
                            </PillButton>
                        )}
                    </DrawerBlock>

                    <SenderLine />
                    <p className={cn(TYPE.tc, "m-0 -mt-1 text-gray-500")}>{name ? `${name} sends it and is the sender on record.` : "You send it and are the sender on record."}</p>
                </>
            )}

            {/* With the Checks card hidden, a failed check is said here as a warning. It no longer holds
                Send, so any other footer message (a send that failed, say) comes first. */}
            <DrawerFoot message={message ?? blockedMessage} first={first} second={second} />

            {editing && value && shown && (
                <EditClientEmailDialog
                    docId={doc.id}
                    value={value}
                    html={shown.html}
                    onSave={(next) => {
                        change(next);
                        setEditing(false);
                    }}
                    onClose={() => setEditing(false)}
                />
            )}

            <TestClientEmailDialog
                open={confirmingTest}
                docId={doc.id}
                subject={value?.subject ?? ""}
                sending={testing}
                failed={testFailure}
                onSend={() => void sendTest()}
                onClose={() => setConfirmingTest(false)}
            />

            <SendClientEmailDialog
                open={confirming}
                docId={doc.id}
                to={people?.draft?.to ?? ([] as DocMessagePerson[])}
                cc={people?.draft?.cc ?? []}
                subject={value?.subject ?? ""}
                sending={sending}
                failed={sendFailure}
                unconfirmed={unconfirmed}
                onSend={(resend) => void send(resend)}
                onClose={() => setConfirming(false)}
            />
        </>
    );
}
